/* eslint-disable ember/no-classic-components */
import { tracked } from "@glimmer/tracking";
import Component from "@ember/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { trackedArray } from "@ember/reactive/collections";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { scheduleOnce } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { tagName } from "@ember-decorators/component";
import { modifier } from "ember-modifier";
import UserAutocompleteResults from "discourse/components/user-autocomplete-results";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { SEND_SHORTCUT_META_ENTER } from "discourse/lib/constants";
import { hashtagAutocompleteOptions } from "discourse/lib/hashtag-autocomplete";
import { TextareaAutocompleteHandler } from "discourse/lib/textarea-text-manipulation";
import UppyUpload from "discourse/lib/uppy/uppy-upload";
import UppyMediaOptimization from "discourse/lib/uppy-media-optimization-plugin";
import userSearch, { validateSearchResult } from "discourse/lib/user-search";
import {
  destroyUserStatuses,
  initUserStatusHtml,
  renderUserStatusHtml,
} from "discourse/lib/user-status-on-autocomplete";
import { clipboardHelpers, slugify } from "discourse/lib/utilities";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dAutocomplete from "discourse/ui-kit/modifiers/d-autocomplete";
import { i18n } from "discourse-i18n";
import AiAgentLlmSelector from "discourse/plugins/discourse-ai/discourse/components/ai-agent-llm-selector";
import AiBotConversationsLayout from "discourse/plugins/discourse-ai/discourse/components/ai-bot-conversations-layout";
import { takeConversationDraft } from "discourse/plugins/discourse-ai/discourse/lib/ai-bot-conversation-draft";

@tagName("")
export default class AiBotConversations extends Component {
  @service aiCredits;
  @service aiBotConversationsHiddenSubmit;
  @service capabilities;
  @service currentUser;
  @service mediaOptimizationWorker;
  @service sessionStore;
  @service site;
  @service siteSettings;
  @service tooltip;

  @tracked creditStatus = null;
  @tracked selectedAgentId = null;
  @tracked selectedLlmId = null;
  @tracked uploads = trackedArray();

  shiftHeldOnEnter = false;

  // Don't track this directly - we'll get it from uppyUpload

  textarea = null;
  uppyUpload = null;
  fileInputEl = null;

  creditLimitTooltipModifier = modifier((element) => {
    if (!this.isSubmitDisabled) {
      return;
    }

    const instance = this.tooltip.register(element, {
      identifier: "ai-credit-limit-tooltip",
      content: trustHTML(
        this.aiCredits.getCreditLimitMessage(this.creditStatus)
      ),
      placement: "top",
      triggers: "hover",
      interactive: true,
      trapTab: false,
    });

    return () => {
      instance.destroy();
    };
  });

  #handlePaste = (event) => {
    if (document.activeElement !== this.textarea) {
      return;
    }

    const { canUpload, canPasteHtml, types } = clipboardHelpers(event, {
      siteSettings: this.siteSettings,
      canUpload: true,
    });

    if (!canUpload || canPasteHtml || types.includes("text/plain")) {
      return;
    }

    if (event && event.clipboardData && event.clipboardData.files) {
      this.uppyUpload.addFiles([...event.clipboardData.files], {
        pasted: true,
      });
    }
  };

  init() {
    super.init(...arguments);

    this.uppyUpload = new UppyUpload(getOwner(this), {
      id: "ai-bot-file-uploader",
      type: "ai-bot-conversation",
      useMultipartUploadsIfAvailable: true,

      uppyReady: () => {
        if (this.siteSettings.composer_media_optimization_image_enabled) {
          this.uppyUpload.uppyWrapper.useUploadPlugin(UppyMediaOptimization, {
            optimizeFn: (data, opts) =>
              this.mediaOptimizationWorker.optimizeImage(data, opts),
            runParallel: !this.capabilities.isMobileDevice,
          });
        }

        this.uppyUpload.uppyWrapper.onPreProcessProgress((file) => {
          const inProgressUpload = this.inProgressUploads?.find(
            (upl) => upl.id === file.id
          );
          if (inProgressUpload && !inProgressUpload.processing) {
            inProgressUpload.processing = true;
          }
        });

        this.uppyUpload.uppyWrapper.onPreProcessComplete((file) => {
          const inProgressUpload = this.inProgressUploads?.find(
            (upl) => upl.id === file.id
          );
          if (inProgressUpload) {
            inProgressUpload.processing = false;
          }
        });

        this.textarea?.addEventListener("paste", this.#handlePaste);
      },

      uploadDone: (upload) => {
        this.uploads.push(upload);
      },

      // Fix: Don't try to set inProgressUploads directly
      onProgressUploadsChanged: () => {
        // This is just for UI triggers - we're already tracking inProgressUploads
        this.notifyPropertyChange("inProgressUploads");
      },
    });
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.textarea?.removeEventListener("paste", this.#handlePaste);
    this.uppyUpload?.teardown();
    // needed for safety (textarea may not have a autocomplete)
    if (this.textarea.autocomplete) {
      this.textarea.autocomplete("destroy");
    }
  }

  get loading() {
    return this.aiBotConversationsHiddenSubmit?.loading;
  }

  get inProgressUploads() {
    return this.uppyUpload?.inProgressUploads || [];
  }

  get showUploadsContainer() {
    return this.uploads?.length > 0 || this.inProgressUploads?.length > 0;
  }

  get isSubmitDisabled() {
    return (
      !this.selectedAgentId ||
      (this.currentUser.ai_available_llm_models?.length > 0 &&
        !this.selectedLlmId) ||
      this.creditStatus?.hard_limit_reached === true
    );
  }

  get sendOnMetaEnter() {
    return (
      this.currentUser?.user_option?.send_shortcut === SEND_SHORTCUT_META_ENTER
    );
  }

  @action
  async setAgentId(id) {
    this.selectedAgentId = id;
    this.aiBotConversationsHiddenSubmit.agentId = id;
    // Only check agent credits if no LLM is explicitly selected
    // (e.g., when agent has force_default_llm)
    if (!this.selectedLlmId) {
      await this.#checkCreditStatus(id, "agent");
    }
  }

  @action
  async setLlmId(llmModelId) {
    this.selectedLlmId = llmModelId;
    this.aiBotConversationsHiddenSubmit.llmModelId = llmModelId;
    await this.#checkCreditStatus(llmModelId, "llm");
  }

  @action
  onSelectionChanged({ agentName, llmName }) {
    if (!this.controller) {
      return;
    }

    this.controller.agent = agentName ? slugify(agentName) || agentName : null;
    this.controller.llm = llmName ? slugify(llmName) || llmName : null;
  }

  @action
  setTargetRecipient(username) {
    this.aiBotConversationsHiddenSubmit.targetUsername = username;
  }

  @action
  updateInputValue(value) {
    this._autoExpandTextarea();
    this.aiBotConversationsHiddenSubmit.inputValue =
      value.target?.value || value;
  }

  @action
  handleKeyDown(event) {
    if (event.target.tagName !== "TEXTAREA") {
      return;
    }
    if (event.key === "Enter") {
      // Meta/ctrl+Enter never produces an insertLineBreak beforeinput, so it
      // must be handled here rather than in handleBeforeInput.
      if (
        this.sendOnMetaEnter &&
        !event.isComposing &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.prepareAndSubmitToBot();
        return;
      }
      this.shiftHeldOnEnter = event.shiftKey;
    }
  }

  @action
  handleBeforeInput(event) {
    // Real Enter on a textarea inserts a line break; IME-confirming Enter
    // inserts composition text. Reading inputType is deterministic across
    // browsers regardless of compositionend/keydown ordering quirks.
    const shiftHeld = this.shiftHeldOnEnter;
    this.shiftHeldOnEnter = false;
    if (
      event.inputType !== "insertLineBreak" ||
      shiftHeld ||
      this.sendOnMetaEnter
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.prepareAndSubmitToBot();
  }

  @action
  setTextArea(element) {
    this.textarea = element;
    this.setupAutocomplete(element);
    this.#restoreDraft();
    scheduleOnce("afterRender", this, this.focusTextarea);
  }

  @action
  focusTextarea() {
    this.textarea?.focus();
  }

  @action
  setupAutocomplete(textarea) {
    this.applyUserAutocomplete(textarea);
    this.applyHashtagAutocomplete(textarea);
  }

  @action
  applyUserAutocomplete(textarea) {
    if (!this.siteSettings.enable_mentions) {
      return;
    }

    const autocompleteHandler = new TextareaAutocompleteHandler(textarea);
    dAutocomplete.setupAutocomplete(
      getOwner(this),
      textarea,
      autocompleteHandler,
      {
        component: UserAutocompleteResults,
        key: UserAutocompleteResults.TRIGGER_KEY,
        dataSource: (term) => {
          destroyUserStatuses();
          return userSearch({
            term,
            includeGroups: true,
          }).then((result) => {
            initUserStatusHtml(getOwner(this), result.users);
            return result;
          });
        },
        onRender: (options) => renderUserStatusHtml(options),
        fixedTextareaPosition: true,
        autoSelectFirstSuggestion: true,
        offset: 2,
        transformComplete: (obj) => {
          validateSearchResult(obj);
          return obj.username || obj.name;
        },
        afterComplete: (text) => {
          this.textarea.value = text;
          this.focusTextarea();
          this.updateInputValue({ target: { value: text } });
        },
        onClose: destroyUserStatuses,
      }
    );
  }

  @action
  applyHashtagAutocomplete(textarea) {
    // Use the "topic-composer" configuration or create a specific one for AI bot
    // You can change this to "chat-composer" if that's more appropriate
    const hashtagConfig = this.site.hashtag_configurations["topic-composer"];

    const autocompleteHandler = new TextareaAutocompleteHandler(textarea);
    dAutocomplete.setupAutocomplete(
      getOwner(this),
      textarea,
      autocompleteHandler,
      hashtagAutocompleteOptions(hashtagConfig, {
        offset: 2,
        fixedTextareaPosition: true,
        afterComplete: (text) => {
          this.textarea.value = text;
          this.focusTextarea();
          this.updateInputValue({ target: { value: text } });
        },
      })
    );
  }

  @action
  registerFileInput(element) {
    if (element) {
      this.fileInputEl = element;
      if (this.uppyUpload) {
        this.uppyUpload.setup(element);
      }
    }
  }

  @action
  openFileUpload() {
    if (this.fileInputEl) {
      this.fileInputEl.click();
    }
  }

  @action
  removeUpload(upload) {
    this.uploads = trackedArray(this.uploads.filter((u) => u !== upload));
  }

  @action
  cancelUpload(upload) {
    this.uppyUpload.cancelSingleUpload({
      fileId: upload.id,
    });
  }

  @action
  async prepareAndSubmitToBot() {
    try {
      await this.aiBotConversationsHiddenSubmit.submitToBot({
        uploads: this.uploads,
        inProgressUploadsCount: this.inProgressUploads.length,
      });
      this.uploads = trackedArray();
    } catch (error) {
      popupAjaxError(error);
    }
  }

  #restoreDraft() {
    const draft = takeConversationDraft(this.sessionStore);
    if (draft) {
      this.textarea.value = draft;
      this.updateInputValue(draft);
    }
  }

  async #checkCreditStatus(id, type) {
    // Clear status immediately to prevent stale data
    this.creditStatus = null;

    if (!id) {
      return;
    }

    try {
      this.creditStatus =
        type === "agent"
          ? await this.aiCredits.getAgentCreditStatus(id)
          : await this.aiCredits.getLlmModelCreditStatus(id);
    } catch {
      // Fail open - allow usage if credit check fails
      this.creditStatus = null;
    }
  }

  _autoExpandTextarea() {
    this.textarea.style.height = "auto";
    this.textarea.style.height = this.textarea.scrollHeight + "px";

    // Get the max-height value from CSS (30vh)
    const maxHeight = parseInt(getComputedStyle(this.textarea).maxHeight, 10);

    // Only enable scrolling if content exceeds max-height
    if (this.textarea.scrollHeight > maxHeight) {
      this.textarea.style.overflowY = "auto";
    } else {
      this.textarea.style.overflowY = "hidden";
    }
  }

  <template>
    <AiBotConversationsLayout
      ...attributes
      @submit={{this.prepareAndSubmitToBot}}
      @updateInput={{this.updateInputValue}}
    >
      <:default>
        <div
          class={{dConcatClass
            "ai-bot-conversations__input-wrapper"
            (if this.isSubmitDisabled "--disabled")
          }}
          {{this.creditLimitTooltipModifier}}
        >
          <DButton
            class="btn btn-transparent ai-bot-upload-btn"
            @action={{unless this.isSubmitDisabled this.openFileUpload}}
            @disabled={{this.isSubmitDisabled}}
            @icon="upload"
            @title="discourse_ai.ai_bot.conversations.upload_files"
          />
          <textarea
            autofocus={{unless this.isSubmitDisabled "true"}}
            disabled={{if this.isSubmitDisabled true this.loading}}
            id="ai-bot-conversations-input"
            minlength="10"
            placeholder={{i18n "discourse_ai.ai_bot.conversations.placeholder"}}
            rows="1"
            {{didInsert this.setTextArea}}
            {{on "input" this.updateInputValue}}
            {{on "keydown" this.handleKeyDown}}
            {{on "beforeinput" this.handleBeforeInput}}
          />
          <DButton
            class="ai-bot-button btn-transparent ai-conversation-submit"
            @action={{unless this.isSubmitDisabled this.prepareAndSubmitToBot}}
            @disabled={{this.isSubmitDisabled}}
            @icon="paper-plane"
            @isLoading={{unless this.isSubmitDisabled this.loading}}
            @title="discourse_ai.ai_bot.conversations.header"
          />
          <input
            class="hidden-upload-field"
            id="ai-bot-file-uploader"
            multiple="multiple"
            type="file"
            {{didInsert this.registerFileInput}}
          />
        </div>

        <AiAgentLlmSelector
          @agentName={{@controller.agent}}
          @llmName={{@controller.llm}}
          @onSelectionChanged={{this.onSelectionChanged}}
          @setAgentId={{this.setAgentId}}
          @setLlmId={{this.setLlmId}}
          @setTargetRecipient={{this.setTargetRecipient}}
          @showLabels={{true}}
        />
      </:default>
      <:footer>
        {{#if this.showUploadsContainer}}
          <div class="ai-bot-conversations__uploads-container">
            {{#each this.uploads as |upload|}}
              <div class="ai-bot-upload">
                <span class="ai-bot-upload__filename">
                  {{upload.original_filename}}
                </span>
                <DButton
                  class="btn-transparent ai-bot-upload__remove"
                  @action={{fn this.removeUpload upload}}
                  @icon="xmark"
                />
              </div>
            {{/each}}

            {{#each this.inProgressUploads as |upload|}}
              <div class="ai-bot-upload ai-bot-upload--in-progress">
                <span class="ai-bot-upload__filename">{{upload.fileName}}</span>
                <span class="ai-bot-upload__progress">
                  {{upload.progress}}%
                </span>
                <DButton
                  class="btn-flat ai-bot-upload__cancel"
                  @action={{fn this.cancelUpload upload}}
                  @icon="xmark"
                />
              </div>
            {{/each}}
          </div>
        {{/if}}
      </:footer>
    </AiBotConversationsLayout>
  </template>
}
